import type { RootState } from '@reducers/index';
import { updateGeo } from '@reducers/search';
import {
  GeoEntry,
  GeoLevel,
  getGeoLevel,
  TuttaItalia,
} from '@sbt-web/networking';
import { assertNever } from '@sbt-web/utils';
import { Button } from '@sbt-web/ui';
import CN from 'classnames';
import React from 'react';
import { connect } from 'react-redux';

import classes from './index.module.scss';
import { getOrCreatePulse } from '@tools/tracking/utils';
import { searchIdKey } from '@client/bridges/constants';

interface ESStateProps {
  currentGeo: GeoEntry;
  searchNearRegions: boolean;
}

interface ESOwnProps {
  withContainer?: boolean;
}

interface ESDispatchProps {
  updateGeo: (geo: GeoEntry, searchNearRegions: boolean) => void;
}

type ESProps = ESOwnProps & ESStateProps & ESDispatchProps;

export const ExpandSearchComponent: React.FC<ESProps> = (props) => {
  const { currentGeo, searchNearRegions, withContainer = false } = props;

  const { buttonText, newGeo, newSearchNearRegions } = escalate(
    currentGeo,
    searchNearRegions
  );

  const onClick = () => {
    props.updateGeo(newGeo, newSearchNearRegions);

    getOrCreatePulse()?.queueEvent({
      type: 'Engagement',
      object: {
        id: 'expanded_geo_search_button',
        type: 'UIElement',
      },
      target: {
        id: '0000000000000000',
        type: 'Listing',
      },
      page: {
        pageType: 'listing',
      },
      search: {
        '@id': sessionStorage.getItem(searchIdKey),
      },
    });
  };

  return currentGeo.region.id != TuttaItalia.id ? (
    <div
      className={CN(classes['expand-search'], {
        [classes.container]: withContainer,
      })}
    >
      {withContainer && (
        <p className="body-text">La tua ricerca ha restituito pochi annunci.</p>
      )}
      <Button design="text" size="small" onClick={onClick}>
        {buttonText}
      </Button>
    </div>
  ) : null;
};

interface EscalationInfo {
  buttonText: string;
  newGeo: GeoEntry;
  newSearchNearRegions: boolean;
}

function escalate(geo: GeoEntry, searchNearRegions: boolean): EscalationInfo {
  const geoLevel = getGeoLevel(geo, searchNearRegions);
  switch (geoLevel) {
    case GeoLevel.Town: {
      return {
        buttonText: 'Espandi a tutta la provincia',
        newGeo: {
          city: geo.city,
          region: geo.region,
        },
        newSearchNearRegions: false,
      };
    }
    case GeoLevel.City: {
      return {
        buttonText: 'Espandi a tutta la regione',
        newGeo: {
          region: geo.region,
        },
        newSearchNearRegions: false,
      };
    }
    case GeoLevel.Region: {
      return {
        buttonText: 'Espandi alle regioni vicine',
        newGeo: {
          region: geo.region,
        },
        newSearchNearRegions: true,
      };
    }
    case GeoLevel.RegionAndNearRegions:
    case GeoLevel.Italia: {
      return {
        buttonText: 'Espandi in tutta Italia',
        newGeo: {
          region: TuttaItalia,
        },
        newSearchNearRegions: false,
      };
    }
    default:
      assertNever(geoLevel as never);
  }
}

export const ExpandSearch = connect<
  ESStateProps,
  ESDispatchProps,
  unknown,
  RootState
>(
  (state: RootState) => ({
    currentGeo: state.search.geo,
    searchNearRegions: state.search.searchNearRegions as boolean,
  }),
  (dispatch) => ({
    updateGeo: (geo: GeoEntry, searchNearRegions: boolean) =>
      dispatch(updateGeo(geo, searchNearRegions)),
  })
)(ExpandSearchComponent);
